import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../../utils/csrf';

interface FormData {
  assetType: string;
  operationType: string;
  operationDate: string;
  ticker: string;
  volume: string;
  investedAmount: string;
  endDate: string;
  rate: string;
}

export const useInvestmentsTesouroDiretoImport = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    assetType: 'tesouro-direto',
    operationType: '',
    operationDate: new Date().toISOString().split('T')[0],
    ticker: '',
    volume: '',
    investedAmount: '',
    endDate: '',
    rate: '',
  });

  const totalSteps = 5;

  const handleSelectOption = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'confirm' && value === 'no') {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const navigateToStep = (step: number) => {
    if (step > currentStep) {
      let canProceed = true;
      
      switch (currentStep) {
        case 1:
          canProceed = !!formData.operationType;
          break;
        case 2:
          canProceed = !!formData.operationDate;
          break;
        case 3:
          canProceed = !!formData.ticker;
          break;
        case 4:
          canProceed = !!formData.volume && !!formData.investedAmount && 
                      !!formData.endDate && !!formData.rate;
          break;
      }

      if (!canProceed) {
        console.warn('Please fill in all required fields before proceeding');
        return;
      }
    }
    
    setCurrentStep(step);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (currentStep === 5) {
      const requiredFields: (keyof FormData)[] = [
        'assetType',
        'operationType',
        'operationDate',
        'ticker',
        'volume',
        'investedAmount',
        'endDate',
        'rate'
      ];

      const emptyFields = requiredFields.filter(field => !formData[field]);
      
      if (emptyFields.length > 0) {
        console.error('Missing required fields:', emptyFields);
        return;
      }
      
      try {
        const csrfToken = await getCsrfToken();
        
        const formPayload = {
          ...formData,
          country: 'Brazil',
          currency: 'brl'
        };

        const response = await fetch('/api/investments/import/', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify(formPayload),
        });

        if (response.ok) {
          navigate('/investments');
        } else {
          const errorData = await response.json();
          console.error('Form submission failed:', errorData.error);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  return {
    currentStep,
    formData,
    totalSteps,
    handleInputChange,
    handleSelectOption,
    navigateToStep,
    handleSubmit,
  };
};
